import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, AlertCircle, CheckCircle2, XCircle, Loader2, Download, Search, RefreshCw } from "lucide-react";
import Papa from "papaparse";
import { useToast } from "@/hooks/use-toast";

interface UntranscribedResponse {
  question_response_id: string;
  test_response_id: string;
  question_id: string;
  candidate_email: string;
  candidate_name: string;
  test_title: string;
  test_id: string;
  created_at: string;
  transcription_status: string;
  transcription_error: string;
  evaluation_status: string;
  video_path: string;
  audio_url: string;
}

interface RetriggerResult {
  success: boolean;
  updated: number;
  total: number;
  errors?: string[];
  transcription?: {
    processed: number;
    succeeded: number;
    failed: number;
    skipped: number;
    duration_ms: number;
    errors?: string[];
  };
}

export default function TranscriptionRecovery() {
  const { toast } = useToast();
  
  // Step 1: Find untranscribed
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(new Date(Date.now() + 86400000).toISOString().split('T')[0]);
  const [searching, setSearching] = useState(false);
  const [untranscribedResults, setUntranscribedResults] = useState<UntranscribedResponse[] | null>(null);
  
  // Step 2: Retrigger
  const [uploadedIds, setUploadedIds] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [retriggering, setRetriggering] = useState(false);
  const [retriggerResult, setRetriggerResult] = useState<RetriggerResult | null>(null);

  // Step 1: Find untranscribed responses
  const handleFindUntranscribed = async () => {
    setSearching(true);
    setUntranscribedResults(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('find-untranscribed-responses', {
        body: { 
          startDate: `${startDate}T00:00:00.000Z`,
          endDate: `${endDate}T23:59:59.999Z`
        }
      });

      if (error) throw error;

      if (data.success) {
        setUntranscribedResults(data.responses);
        toast({
          title: "Search Complete",
          description: `Found ${data.count} untranscribed responses`,
        });
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (error: any) {
      console.error('Error finding untranscribed:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSearching(false);
    }
  };

  // Download CSV
  const downloadUntranscribedCSV = () => {
    if (!untranscribedResults) return;
    
    const csv = Papa.unparse(untranscribedResults);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `untranscribed-responses-${startDate}-to-${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Step 2: Handle CSV upload
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const parseCSV = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const ids = results.data
          .map((row: any) => row.question_response_id)
          .filter((id: string) => id && id.trim());
        
        setUploadedIds(ids);
        setRetriggerResult(null);
        
        toast({
          title: "CSV Loaded",
          description: `Found ${ids.length} question response IDs`,
        });
      },
      error: (error) => {
        toast({
          title: "Parse Error",
          description: error.message,
          variant: "destructive",
        });
      }
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file && file.type === "text/csv") {
      parseCSV(file);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      parseCSV(file);
    }
  };

  // Retrigger transcription
  const handleRetriggerTranscription = async () => {
    if (uploadedIds.length === 0) return;
    
    setRetriggering(true);
    setRetriggerResult(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('trigger-batch-evaluation', {
        body: { question_response_ids: uploadedIds }
      });

      if (error) throw error;

      setRetriggerResult({
        success: data.success,
        updated: data.updated,
        total: data.total,
        errors: data.errors
      });

      toast({
        title: data.success ? "Retrigger Complete" : "Retrigger Completed with Errors",
        description: `Updated ${data.updated}/${data.total} responses`,
        variant: data.success ? "default" : "destructive",
      });
    } catch (error: any) {
      console.error('Error retriggering:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setRetriggering(false);
    }
  };

  // Use results from Step 1 directly
  const useStep1Results = () => {
    if (!untranscribedResults) return;
    const ids = untranscribedResults.map(r => r.question_response_id);
    setUploadedIds(ids);
    setRetriggerResult(null);
    toast({
      title: "IDs Loaded",
      description: `Loaded ${ids.length} IDs from search results`,
    });
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Find Untranscribed */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Step 1: Find Untranscribed Responses
          </CardTitle>
          <CardDescription>
            Search for question responses with video/audio that haven't been transcribed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <Button onClick={handleFindUntranscribed} disabled={searching}>
            {searching ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Find Untranscribed
              </>
            )}
          </Button>

          {/* Results */}
          {untranscribedResults && (
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Found <strong>{untranscribedResults.length}</strong> untranscribed responses
                </AlertDescription>
              </Alert>

              <div className="flex gap-2">
                <Button variant="outline" onClick={downloadUntranscribedCSV}>
                  <Download className="mr-2 h-4 w-4" />
                  Download CSV
                </Button>
                <Button variant="secondary" onClick={useStep1Results}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Use for Step 2
                </Button>
              </div>

              {untranscribedResults.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[300px] overflow-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                          <TableHead>Response ID</TableHead>
                          <TableHead>Candidate</TableHead>
                          <TableHead>Test</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {untranscribedResults.slice(0, 100).map((r) => (
                          <TableRow key={r.question_response_id}>
                            <TableCell className="font-mono text-xs">
                              {r.question_response_id.slice(0, 8)}...
                            </TableCell>
                            <TableCell className="text-sm">
                              {r.candidate_email}
                            </TableCell>
                            <TableCell className="text-sm max-w-[200px] truncate">
                              {r.test_title}
                            </TableCell>
                            <TableCell className="text-xs">
                              {new Date(r.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-xs">
                              {r.transcription_status}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {untranscribedResults.length > 100 && (
                    <div className="p-2 text-center text-sm text-muted-foreground border-t">
                      Showing first 100 of {untranscribedResults.length} results. Download CSV for full list.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2: Retrigger Transcription */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Step 2: Retrigger Transcription
          </CardTitle>
          <CardDescription>
            Upload a CSV with question_response_id column to retrigger transcription and evaluation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Upload Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : 'border-border'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="mb-4 text-sm text-muted-foreground">
              Drag and drop CSV file, or click to browse
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileInput}
              className="hidden"
              id="retrigger-file-upload"
            />
            <label htmlFor="retrigger-file-upload">
              <Button variant="outline" asChild>
                <span>Choose File</span>
              </Button>
            </label>
          </div>

          {/* Preview */}
          {uploadedIds.length > 0 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>{uploadedIds.length} IDs loaded.</strong> Ready to retrigger transcription.
                <div className="mt-2 text-xs text-muted-foreground">
                  First 3: {uploadedIds.slice(0, 3).map(id => id.slice(0, 8) + '...').join(', ')}
                  {uploadedIds.length > 3 ? '...' : ''}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Retrigger Button */}
          {uploadedIds.length > 0 && !retriggering && (
            <Button onClick={handleRetriggerTranscription} className="w-full">
              <RefreshCw className="mr-2 h-4 w-4" />
              Retrigger Transcription for {uploadedIds.length} Responses
            </Button>
          )}

          {/* Progress */}
          {retriggering && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Processing transcriptions with parallel execution... This may take a few minutes for large batches.
              </AlertDescription>
            </Alert>
          )}

          {/* Results */}
          {retriggerResult && (
            <div className="space-y-4">
              {/* Reset Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Reset
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {retriggerResult.updated}
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-blue-600" />
                      Total
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {retriggerResult.total}
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-600" />
                      Errors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600">
                      {retriggerResult.errors?.length || 0}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Transcription Processing Results */}
              {retriggerResult.transcription && (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">Transcription Processing Results</h4>
                  <div className="grid grid-cols-4 gap-3">
                    <Card className="border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20">
                      <CardContent className="pt-4">
                        <div className="text-lg font-bold text-green-600">
                          {retriggerResult.transcription.succeeded}
                        </div>
                        <div className="text-xs text-muted-foreground">Succeeded</div>
                      </CardContent>
                    </Card>
                    <Card className="border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20">
                      <CardContent className="pt-4">
                        <div className="text-lg font-bold text-red-600">
                          {retriggerResult.transcription.failed}
                        </div>
                        <div className="text-xs text-muted-foreground">Failed</div>
                      </CardContent>
                    </Card>
                    <Card className="border-yellow-200 bg-yellow-50/50 dark:border-yellow-900 dark:bg-yellow-950/20">
                      <CardContent className="pt-4">
                        <div className="text-lg font-bold text-yellow-600">
                          {retriggerResult.transcription.skipped}
                        </div>
                        <div className="text-xs text-muted-foreground">Skipped</div>
                      </CardContent>
                    </Card>
                    <Card className="border-muted">
                      <CardContent className="pt-4">
                        <div className="text-lg font-bold">
                          {(retriggerResult.transcription.duration_ms / 1000).toFixed(1)}s
                        </div>
                        <div className="text-xs text-muted-foreground">Duration</div>
                      </CardContent>
                    </Card>
                  </div>

                  {retriggerResult.transcription.errors && retriggerResult.transcription.errors.length > 0 && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Transcription Errors:</strong>
                        <ul className="mt-2 list-disc list-inside text-xs">
                          {retriggerResult.transcription.errors.slice(0, 10).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                          {retriggerResult.transcription.errors.length > 10 && (
                            <li>...and {retriggerResult.transcription.errors.length - 10} more</li>
                          )}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {retriggerResult.errors && retriggerResult.errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Errors:</strong>
                    <ul className="mt-2 list-disc list-inside text-xs">
                      {retriggerResult.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {retriggerResult.errors.length > 5 && (
                        <li>...and {retriggerResult.errors.length - 5} more</li>
                      )}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {retriggerResult.transcription && retriggerResult.transcription.succeeded > 0 && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    {retriggerResult.transcription.succeeded} responses transcribed successfully. 
                    Evaluation will proceed automatically via the evaluate-response chain.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
